import axios, {CancelToken} from 'axios';
import {AsyncCookie, detectIE, formatDate, getRedirectToString, toRelativeUrl} from '@/components/shared/client/utils'
import {getModuleNameForAdmin} from './variables.ts';
import {BASE_PATH_NAME} from "@/components/constants";

export const http = axios.create({});

export const getHeaders = async (method) => {
    const headers = {};
    if (method === 'post' || method === 'put' || method === 'patch') {
        headers['Content-Type'] = 'application/json';
    }
    if (detectIE() && method === 'get') {
        headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        headers['Pragma'] = 'no-cache';
        headers['Expires'] = '0';
    }
    return headers;
};

const extractErrorMessageFromError = (error) => {
    if (error.message) return error.message
    if (error.code) return error.code
    if (error.description) return error.description
    return JSON.stringify(error)
}

const extractStringError = (response) => {
    let err = response.data
    if (response?.data?.errors && Array.isArray(response.data.errors)) {
        err = "";
        if (response.data.message) {
            err += response.data.message + ","
        }
        for (let error of response.data.errors) {
            err += extractErrorMessageFromError(error) + ", ";
        }
    } else {

        if (/(<([^>]+)>)/i.test(response.data)) {
            err = `Request failed with status code ${response.status}`
        } else {
            err = extractErrorMessageFromError(response.data)
        }
    }
    return err
}


export const getError = (res) => {
    let response = res.response;
    if (response?.data) {
        let otherError = new Error(extractStringError(response));
        otherError.status = response.status;
        return otherError;
    }
    if (response?.statusText) {
        let err = new Error(response.statusText);
        err.status = response.status;
        return err
    }
    let err = null;
    err = new Error(res || 'HTTP Error');
    err.status = 0;
    return err;
};

export let cancelRequest = CancelToken;

http.interceptors.request.use(
    async (config) => {
        config.method = config.method || 'get';
        config.headers = await getHeaders(config.method);
        config.cancelToken = config.cancelToken || new CancelToken((c) => {
            cancelRequest = c;
        });
        config.onUploadProgress = (progressEvent) => {
            // if (config.url === "/mintra/api/admin/videos/files") {
            //     store.dispatch(uploadProgressChange(progressEvent));
            // }
        }
        if (config.params && config.params.responseType) {
            config.responseType = config.params.responseType;
        }
        return config;
    },
    (error) => {
        let err = getError(error);
        if (err.status === 401) {
            // store.dispatch(userActions.logoutUserOn401());
        }
        return Promise.reject(err);
    },
);

export const request = async (opts, responseSelector) => {
    try {
        const response = await http(opts);
        if (response.status === 201 && response.headers.location) {
            return toRelativeUrl(response.headers.location);
        }
        return responseSelector(response);
    } catch (error) {
        let err = getError(error);
        return Promise.reject(err);
    }
};

const fullResponseSelector = (res) => {
    return res;
};
const dataResponseSelector = (res) => {
    return res.data;
};

export const get = (url, params) => request({url, params}, dataResponseSelector);
export const post = (url, data, config) => {
    return request({method: 'post', url, data}, dataResponseSelector, config);
};
export const put = (url, data) => request({method: 'put', url, data}, dataResponseSelector);
export const patch = (url, data) => request({method: 'patch', url, data}, dataResponseSelector);
export const remove = (url, data) => request({method: 'delete', url, data}, dataResponseSelector);

export const getFullResponse = (url, params) => request({url, params}, fullResponseSelector);
export const postFullResponse = (url, data) => request({
    method: 'post',
    url,
    data
}, fullResponseSelector);
export const putFullResponse = (url, data) => request({
    method: 'put',
    url,
    data
}, fullResponseSelector);
export const patchFullResponse = (url, data) => request({
    method: 'patch',
    url,
    data
}, fullResponseSelector);
export const removeFullResponse = (url, data) => request({
    method: 'delete',
    url,
    data
}, fullResponseSelector);

const HTTPClient = (time: number) => {
    return axios.create({
        baseURL: process.env.NEXT_PUBLIC_LOCAL_DOMAIN,
        timeout: 60 * time * 1000,
        withCredentials: true,
        headers: {
            "Content-type": "application/json",
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Credentials": true
        },
    });
}

export const cleanPath = (path: string, prefix: string) => {
    const hostname = window.location.hostname;
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`^\\/?${escaped}|\\/$`, 'g');
    let cp = path.replace(regex, '').replace(/^\/+|\/+$/g, '');
    if(Boolean(hostname)) {
        if (hostname.startsWith('apply.')) cp = `students/${cp}`;
        if (hostname.startsWith('indc.')) cp = `admin/${cp}`;
    }
    return cp;
}

export const serverCall = async (obj, onUploadProgress) => {
    const httpClient = HTTPClient(obj.timeout);
    const type = obj.type ? obj.type.toLowerCase() : obj.fileUpload ? "post" : "get";
    let url = `${process.env.NEXT_PUBLIC_SERVER_DOMAIN}${obj.apiDir}${obj.endpoint}`;
    let params = {
        ...obj.data,
        timeZoneDifference: new Date().getTimezoneOffset(),
        licenseKey: process.env.NEXT_PUBLIC_LICENSE_KEY,
        requestURL: cleanPath(getModuleNameForAdmin()[0], BASE_PATH_NAME),
        pathname: cleanPath(getModuleNameForAdmin()[0], BASE_PATH_NAME),
        fetchType: obj.data?.fetchType === "noLazy" ? null : "lazy",
        siteCookiePrefix: process.env.NEXT_PUBLIC_COOKIE_PREFIX,
        redirectTo: getRedirectToString(window.location.href),
        sessionId: AsyncCookie.getCookie(`${process.env.NEXT_PUBLIC_COOKIE_PREFIX}_SESSION_ID`),
        hostname: window.location.origin
    }

    if (obj.fileUpload) {
        let formData = new FormData();
        const files = obj.files;
        for (let x = 0; x < files.length; x++) {
            formData.append("imageFile", files[x]);
        }
        const paramsObject = {
            headers: {"Content-Type": "multipart/form-data"},
            params,
            onUploadProgress,
        }

        if (type === "post") {
            return httpClient.post(url, formData, paramsObject);
        } else if (type === "put") {
            return httpClient.put(url, formData, paramsObject);
        } else if (type === "patch") {
            return httpClient.patch(url, formData, paramsObject);
        }
    } else {
        const paramsObject = {
            ...params
        }
        if (type === "get") {
            return httpClient.get(url, {params});
        } else if (type === "post") {
            return httpClient.post(url, paramsObject, {onUploadProgress});
        } else if (type === "put") {
            return httpClient.put(url, paramsObject, {onUploadProgress});
        } else if (type === "delete") {
            return httpClient.delete(url, {params});
        } else if (type === "patch") {
            return httpClient.patch(url, paramsObject, {onUploadProgress});
        }
    }
};
